import { create } from "zustand";
import { resolveNavTitle } from "../config/navigation";

export interface ShellTab {
  id: string;
  path: string;
  title: string;
}

interface PersistedTabs {
  tabs: ShellTab[];
  activeId: string;
}

const STORAGE_KEY = "gaole_bo_shell_tabs";

function newTabId(): string {
  return crypto.randomUUID();
}

function persist(state: { tabs: ShellTab[]; activeId: string }): void {
  try {
    const payload: PersistedTabs = { tabs: state.tabs, activeId: state.activeId };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

function loadPersisted(): PersistedTabs | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedTabs;
    if (!Array.isArray(parsed.tabs) || parsed.tabs.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function createTab(path: string): ShellTab {
  return { id: newTabId(), path, title: resolveNavTitle(path) };
}

interface ShellTabsState {
  tabs: ShellTab[];
  activeId: string;
  initialized: boolean;
  initFromPath: (path: string) => void;
  openInActiveTab: (path: string) => string;
  openInNewTab: (path: string) => string;
  setActiveTabTitle: (title: string) => void;
  setActiveTab: (id: string) => string | null;
  closeTab: (id: string) => string | null;
  syncActiveTabFromPath: (path: string) => void;
  activeTab: () => ShellTab | undefined;
}

export const useShellTabs = create<ShellTabsState>((set, get) => ({
  tabs: [],
  activeId: "",
  initialized: false,

  initFromPath: (path) => {
    if (get().initialized) return;

    const saved = loadPersisted();
    if (saved) {
      const activeExists = saved.tabs.some((tab) => tab.id === saved.activeId);
      const activeId = activeExists ? saved.activeId : saved.tabs[0]!.id;
      const tabs = saved.tabs.map((tab) =>
        tab.id === activeId ? { ...tab, path, title: resolveNavTitle(path) } : tab,
      );
      set({ tabs, activeId, initialized: true });
      persist({ tabs, activeId });
      return;
    }

    const tab = createTab(path);
    set({ tabs: [tab], activeId: tab.id, initialized: true });
    persist({ tabs: [tab], activeId: tab.id });
  },

  openInActiveTab: (path) => {
    const { activeId, tabs } = get();
    const title = resolveNavTitle(path);
    const nextTabs = tabs.map((tab) => (tab.id === activeId ? { ...tab, path, title } : tab));
    set({ tabs: nextTabs });
    persist({ tabs: nextTabs, activeId });
    return path;
  },

  openInNewTab: (path) => {
    const tab = createTab(path);
    const tabs = [...get().tabs, tab];
    set({ tabs, activeId: tab.id });
    persist({ tabs, activeId: tab.id });
    return path;
  },

  setActiveTabTitle: (title) => {
    const { activeId, tabs } = get();
    const nextTabs = tabs.map((tab) => (tab.id === activeId ? { ...tab, title } : tab));
    set({ tabs: nextTabs });
    persist({ tabs: nextTabs, activeId });
  },

  setActiveTab: (id) => {
    const tab = get().tabs.find((entry) => entry.id === id);
    if (!tab) return null;
    set({ activeId: id });
    persist({ tabs: get().tabs, activeId: id });
    return tab.path;
  },

  closeTab: (id) => {
    const { tabs, activeId } = get();
    if (tabs.length <= 1) return tabs[0]?.path ?? "/";

    const index = tabs.findIndex((tab) => tab.id === id);
    if (index < 0) return null;

    const nextTabs = tabs.filter((tab) => tab.id !== id);
    let nextActiveId = activeId;
    if (activeId === id) {
      const neighbor = nextTabs[Math.min(index, nextTabs.length - 1)]!;
      nextActiveId = neighbor.id;
    }

    set({ tabs: nextTabs, activeId: nextActiveId });
    persist({ tabs: nextTabs, activeId: nextActiveId });
    return nextTabs.find((tab) => tab.id === nextActiveId)?.path ?? "/";
  },

  syncActiveTabFromPath: (path) => {
    if (!get().initialized) return;
    const { activeId, tabs } = get();
    const active = tabs.find((tab) => tab.id === activeId);
    if (!active || active.path === path) return;

    const title = resolveNavTitle(path);
    const nextTabs = tabs.map((tab) => (tab.id === activeId ? { ...tab, path, title } : tab));
    set({ tabs: nextTabs });
    persist({ tabs: nextTabs, activeId });
  },

  activeTab: () => get().tabs.find((tab) => tab.id === get().activeId),
}));
